import { classNames } from "@NBCUDTC/reactjs-utility-belt/classnames";
import Button from "@NBCUDTC/reactjs-utility-belt/components/atoms/Button";
import Icon from "@NBCUDTC/reactjs-utility-belt/components/atoms/Icon";
import { useEffect, useState } from "react";

import { Image } from "@/components/atoms/Image";
import {
  isLink,
  isSportsEvent,
} from "@/utils/FetchAssetDetails/types/type-guards";
import { CoreBadge } from "@/components/atoms/CoreBadge";
import {
  CoreBadgeSize,
  CoreBadgeVariant,
} from "@/components/atoms/CoreBadge/types";
import {
  formatTitleBasicsDate,
  getEventStartTimeAgo,
  getProviderLogo,
  getShowcaseButtonContent,
} from "@/utils/TileUtils";
import { StreamingIcons } from "@/assets/iconFont/streaming-icons";
import { fetchTuneInBadgeDetails } from "@/utils/FetchTuneInBadgeDetails";
import { Text } from "@/components/atoms/Text";
import { getImageUrl } from "@/utils/GetImageUrl";
import { ImageType } from "@/utils/shared-atom-types";
import { TileTitleBasics } from "@/components/molecules/TileTitleBasics";
import { Tile169Size } from "@/components/molecules/Tile169/types";
import { getTileFormat } from "@/utils/GetTileFormat";
import { providerLogoTheme, TileFormat } from "@/utils/GetTileFormat/types";
import { useClassNames } from "@/utils/classNames";

import { ShowcaseCarouselProps } from "./types";
import styles from "./ShowcaseCarousel.module.scss";

/**
 * ShowcaseCarousel component to display a carousel of featured assets with prominent visual treatment.
 *
 * @param asset - The asset to feature in the carousel
 * @param format - The format of the tile (e.g., VOD, SLE, Channels, Collection, Live, Upcoming)
 * @param className - Additional CSS class for styling
 * @param attrs - Additional HTML attributes for the div element
 * @returns A showcase carousel component featuring multiple assets
 *
 * @example
 * ```tsx
 * <ShowcaseCarousel
 *   asset={assetResponse}
 * />
 * ```
 */
const ShowcaseCarousel = ({
  asset,
  format,
  className,
  ...attrs
}: ShowcaseCarouselProps) => {
  const [tuneInBadgeText, setTuneInBadgeText] = useState<
    string | string[] | null
  >(null);

  if (format === undefined) {
    format = getTileFormat(asset);
  }
  const isVOD = format === TileFormat.VOD;
  const isLive = format === TileFormat.Live;
  const isUpcoming = format === TileFormat.Upcoming;
  const isReplay = format === TileFormat.Replay;
  const isCollection = format === TileFormat.Collection;
  const isHub = format === TileFormat.Hub;
  const isChannel = format === TileFormat.Channel;

  //If asset is Collection or Hub (isLink), use "scene34", otherwise use "nonTitleArt34"
  const imageType =
    (asset as any).imageType ||
    (() => {
      if (isLink(asset)) return "scene34";
      return "nonTitleArt34";
    })();
  const imageUrl = getImageUrl(asset, imageType, {
    width: 750,
    height: 1000,
    quality: 7,
  });

  const cn = useClassNames(styles, styles["showcase-carousel"], className);

  useEffect(() => {
    const fetchBadgeText = async () => {
      try {
        const result = await fetchTuneInBadgeDetails(asset, false);
        setTuneInBadgeText(result);
      } catch (error) {
        setTuneInBadgeText(null);
      }
    };

    fetchBadgeText();
  }, [asset]);

  const providerLogo = getProviderLogo(asset, providerLogoTheme.Dark);
  const titleLogoUrl = getImageUrl(asset, ImageType.TitleLogo, {
    width: 500,
    height: 200,
    quality: 7,
  });

  const buttonContent = getShowcaseButtonContent(format);
  const sleDate = formatTitleBasicsDate(asset);

  const getSleEyebrow = () => {
    if (isLive)
      return (
        <>
          <CoreBadge variant={CoreBadgeVariant.Live}>Live</CoreBadge>
          {getEventStartTimeAgo(asset)}
        </>
      );
    if (isUpcoming && sleDate)
      return (
        <>
          <Icon
            className={styles["upcoming-icon"]}
            icon={StreamingIcons.Clock}
          />
          {sleDate.toString().replace(",", " ")}
        </>
      );
    if (isReplay && sleDate)
      return (
        <>
          <CoreBadge
            className={styles["replay-core-badge"]}
            variant={CoreBadgeVariant.White}
            size={CoreBadgeSize.Size20}
          >
            Replay
          </CoreBadge>
          {sleDate.toString()}
        </>
      );
  };

  const tagline =
    (isCollection || isHub) && "tagline" in asset.attributes
      ? asset.attributes.tagline
      : null;

  return (
    <div className={cn} {...attrs}>
      {imageUrl && (
        <Image src={imageUrl} className={styles["showcase-carousel-image"]} />
      )}
      <div className={styles["image-gradient-mask"]} />
      {providerLogo && (
        <>
          <Image
            src={providerLogo}
            className={styles["provider-logo"]}
            alt="Provider Logo"
          />
          <div className={styles["provider-logo-gradient"]} />
        </>
      )}
      <div
        className={classNames(
          styles["metadata-overlay"],
          isChannel && styles["format-Channel"],
        )}
      >
        {(isSportsEvent(asset) || isChannel) && (
          <Text className={classNames(styles["live-eyebrow"])}>
            {getSleEyebrow()}
          </Text>
        )}
        {(isCollection || isHub) && (
          <Text className={styles["eyebrow"]}>
            {isCollection ? "Collection" : "Hub"}
          </Text>
        )}
        {isChannel && (
          <CoreBadge
            className={styles["channel-badge"]}
            variant={CoreBadgeVariant.TuneIn}
            size={CoreBadgeSize.Size16}
          >
            On Demand
          </CoreBadge>
        )}
        {isChannel ? (
          <Image className={styles["channel-logo"]} src={titleLogoUrl} />
        ) : titleLogoUrl ? (
          <Image className={styles["title-logo"]} src={titleLogoUrl} />
        ) : (
          <Text className={styles["fallback-title"]}>
            {asset.attributes.title}
          </Text>
        )}
        {!isSportsEvent(asset) && (
          <div
            className={classNames(
              styles["metadata"],
              isCollection && styles["collection-metadata"],
            )}
          >
            {isVOD && tuneInBadgeText && (
              <CoreBadge
                className={styles["tune-in-badge"]}
                variant={CoreBadgeVariant.Contextual}
              >
                {tuneInBadgeText}
              </CoreBadge>
            )}
            {(isVOD || isChannel) && (
              <TileTitleBasics
                className={styles["tile-title-basics"]}
                asset={asset}
                title={asset.attributes.title}
                tileSize={Tile169Size.Medium}
              />
            )}
            {tagline && <Text className={styles["tagline"]}>{tagline}</Text>}
          </div>
        )}
        <Button
          className={styles["showcase-carousel-button"]}
          variant="primary"
        >
          {buttonContent.icon && <Icon icon={buttonContent.icon} />}
          {buttonContent.text}
        </Button>
      </div>
    </div>
  );
};

export { ShowcaseCarousel };
